import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BarChart3,
  Calendar,
  Eye,
  RefreshCw,
  X,
} from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { MarketingEmailAnalytics, MarketingEmailStats } from '../../../models/MasterAdmin/marketingEmail';
import {
  fetchAnalytics,
  fetchBrevoDailyReports,
  fetchBrevoEventAnalytics,
  syncAnalyticsFromBrevo,
} from '../../../services/MasterAdmin/marketingEmailService';
import {
  meCardClass,
  meGhostBtnClass,
  meMainPanelClass,
  mePanelHeaderClass,
  mePanelTitleClass,
  meSecondaryBtnClass,
} from './marketingEmailUi';

interface EmailAnalyticsPanelProps {
  onClose: () => void;
}

type BrevoDailyReport = {
  date: string;
  requests: number;
  delivered: number;
  uniqueOpens: number;
  uniqueClicks: number;
  hardBounces: number;
  softBounces: number;
  unsubscribed: number;
};

type BrevoEventRow = {
  event: string;
  date: string;
  subject?: string;
  from?: string;
  email?: string;
  tag?: string;
};

const DAY_OPTIONS = [7, 14, 30] as const;

function pct(numerator: number, denominator: number): number {
  if (!denominator) return 0;
  return Number(((numerator / denominator) * 100).toFixed(2));
}

function formatEventDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString([], {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function eventLabel(event: string): string {
  const map: Record<string, string> = {
    opened: 'Opened',
    clicks: 'Clicked',
    click: 'Clicked',
    delivered: 'Delivered',
    unsubscribed: 'Unsubscribed',
    hardBounces: 'Hard bounce',
    hardBounce: 'Hard bounce',
    softBounces: 'Soft bounce',
    softBounce: 'Soft bounce',
    blocked: 'Blocked',
    requests: 'Sent',
    spam: 'Complaint',
    invalid: 'Invalid',
    deferred: 'Deferred',
    loadedByProxy: 'Loaded by proxy',
  };
  return map[event] || event.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

function eventBadgeClass(event: string): string {
  const e = event.toLowerCase();
  if (e.includes('open')) return 'bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-300';
  if (e.includes('click')) return 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300';
  if (e === 'delivered') return 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300';
  if (e.includes('bounce') || e === 'blocked' || e === 'invalid') return 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300';
  if (e === 'unsubscribed' || e === 'spam') return 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
}

function MetricWithBar({
  label,
  percent,
  color,
}: {
  label: string;
  percent: number;
  color: string;
}) {
  return (
    <div className={`${meCardClass} p-4`}>
      <p className="text-2xl font-semibold text-gray-900 dark:text-white">{percent.toFixed(2)}%</p>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.min(100, percent)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function MetricWithDot({
  label,
  percent,
  color,
}: {
  label: string;
  percent: number;
  color: string;
}) {
  return (
    <div className={`${meCardClass} p-4`}>
      <p className="text-2xl font-semibold text-gray-900 dark:text-white">{percent.toFixed(2)}%</p>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <div className="relative mt-4 h-2 rounded-full bg-gray-100 dark:bg-gray-800">
        <span
          className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full"
          style={{ left: `calc(${Math.min(100, percent)}% - 6px)`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function buildRates(stats: MarketingEmailStats) {
  const sent = Number(stats.requests || 0);
  const delivered = Number(stats.delivered || 0);
  return {
    sent,
    delivered,
    deliveredRate: pct(delivered, sent),
    openRate: pct(stats.opened || 0, delivered),
    clickRate: pct(stats.clicked || 0, delivered),
    bounceRate: pct(stats.bounced || 0, sent),
    hardBounceRate: pct(stats.hardBounced || 0, sent),
    softBounceRate: pct(stats.softBounced || 0, sent),
    blockedRate: pct(stats.blocked || 0, sent),
    complaintRate: pct(stats.spamComplaints || 0, delivered),
    unsubscribeRate: pct(stats.unsubscribed || 0, delivered),
  };
}

const EmailAnalyticsPanel: React.FC<EmailAnalyticsPanelProps> = ({ onClose }) => {
  const [days, setDays] = useState<(typeof DAY_OPTIONS)[number]>(7);
  const [analytics, setAnalytics] = useState<MarketingEmailAnalytics | null>(null);
  const [reports, setReports] = useState<BrevoDailyReport[]>([]);
  const [events, setEvents] = useState<BrevoEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, reportsRes, eventsRes] = await Promise.all([
        fetchAnalytics({ days }),
        fetchBrevoDailyReports({ days, limit: days }),
        fetchBrevoEventAnalytics({ days, limit: 100, tags: 'marketing-email' }),
      ]);
      setAnalytics(analyticsRes.data);
      const reportRows = (reportsRes.data?.reports || []) as BrevoDailyReport[];
      setReports([...reportRows].sort((a, b) => a.date.localeCompare(b.date)));
      const eventRows = (eventsRes.data?.events || []) as BrevoEventRow[];
      setEvents(eventRows);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncAnalyticsFromBrevo({ days, limit: 2500, maxPages: 10 });
      if (res.data?.analytics) setAnalytics(res.data.analytics);
      await loadData();
      toast.success(res.message || 'Synced from Brevo');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const stats = analytics?.aggregateStats;
  const rates = useMemo(() => (stats ? buildRates(stats) : null), [stats]);

  const chartData = useMemo(
    () =>
      reports.map((r) => ({
        date: r.date,
        Sent: r.requests || 0,
        Delivered: r.delivered || 0,
        Opens: r.uniqueOpens || 0,
        Clicks: r.uniqueClicks || 0,
        Bounced: (r.hardBounces || 0) + (r.softBounces || 0),
      })),
    [reports]
  );

  const rangeLabel = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    const fmt = (d: Date) =>
      d.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${fmt(start)} - ${fmt(end)}`;
  }, [days]);

  return (
    <div className={meMainPanelClass}>
      <div className={mePanelHeaderClass}>
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-gray-500" />
          <h2 className={mePanelTitleClass}>Statistics</h2>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={handleSync} disabled={syncing} className={meSecondaryBtnClass}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync from Brevo'}
          </button>
          <button type="button" onClick={onClose} className={meGhostBtnClass}>
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-50/40 p-5 dark:bg-gray-950/40">
        <div className="mx-auto max-w-6xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Calendar className="h-4 w-4" />
              <span>{rangeLabel}</span>
              {analytics?.statsSource && (
                <span className="rounded-full bg-white px-2 py-0.5 text-xs dark:bg-gray-900">
                  Source: {analytics.statsSource === 'brevo' ? 'Brevo live' : 'Database'}
                </span>
              )}
            </div>
            <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 dark:border-gray-700 dark:bg-gray-900">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                    days === d
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  {d} days
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="space-y-4">
              <div className="h-10 w-48 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-800" />
              <div className="grid gap-3 sm:grid-cols-3">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-800" />
                ))}
              </div>
              <div className="h-72 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-800" />
            </div>
          ) : stats && rates ? (
            <>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                {rates.sent.toLocaleString()} emails sent
              </h3>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <MetricWithBar label="Delivered" percent={rates.deliveredRate} color="#3b82f6" />
                <MetricWithBar label="Estimated openers" percent={rates.openRate} color="#14b8a6" />
                <MetricWithDot label="Unique clickers" percent={rates.clickRate} color="#84cc16" />
                <MetricWithDot label="Bounced" percent={rates.bounceRate} color="#ef4444" />
                <MetricWithDot label="Complaint" percent={rates.complaintRate} color="#9ca3af" />
                <MetricWithBar label="Trackable openers" percent={rates.openRate} color="#22c55e" />
                <MetricWithDot label="Hard bounce" percent={rates.hardBounceRate} color="#ef4444" />
                <MetricWithDot label="Soft bounce" percent={rates.softBounceRate} color="#f97316" />
                <MetricWithDot label="Blocked" percent={rates.blockedRate} color="#6b7280" />
              </div>

              <div className={`${meCardClass} bg-white p-4 dark:bg-gray-900`}>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="Sent" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Delivered" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Opens" stroke="#14b8a6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Clicks" stroke="#eab308" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Bounced" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className={`${meCardClass} overflow-hidden bg-white dark:bg-gray-900`}>
                <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Messages from {rangeLabel}
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-950 dark:text-gray-400">
                      <tr>
                        <th className="px-4 py-3 font-medium">Events</th>
                        <th className="px-4 py-3 font-medium">Date</th>
                        <th className="px-4 py-3 font-medium">Subject</th>
                        <th className="px-4 py-3 font-medium">From</th>
                        <th className="px-4 py-3 font-medium">To</th>
                        <th className="px-4 py-3 font-medium">Tags</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {events.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                            No events in this period. Try syncing from Brevo or send a test campaign.
                          </td>
                        </tr>
                      ) : (
                        events.map((row, idx) => (
                          <tr key={`${row.date}-${row.email}-${row.event}-${idx}`} className="hover:bg-gray-50/80 dark:hover:bg-gray-950/50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Eye className="h-3.5 w-3.5 text-gray-400" />
                                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${eventBadgeClass(row.event)}`}>
                                  {eventLabel(row.event)}
                                </span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-300">
                              {formatEventDate(row.date)}
                            </td>
                            <td className="max-w-[200px] truncate px-4 py-3 text-gray-900 dark:text-white">
                              {row.subject || '—'}
                            </td>
                            <td className="max-w-[160px] truncate px-4 py-3 text-gray-600 dark:text-gray-300">
                              {row.from || '—'}
                            </td>
                            <td className="max-w-[180px] truncate px-4 py-3 text-gray-600 dark:text-gray-300">
                              {row.email || '—'}
                            </td>
                            <td className="max-w-[120px] truncate px-4 py-3 text-xs text-gray-500">
                              {row.tag || '—'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-4">
                {[
                  { label: 'Unsubscribed', value: stats.unsubscribed, rate: rates.unsubscribeRate },
                  { label: 'Campaigns sent', value: analytics.sentCampaigns },
                  { label: 'Recipients', value: analytics.totalRecipientsSent },
                  { label: 'Saved contacts', value: analytics.totalContacts },
                ].map(({ label, value, rate }) => (
                  <div key={label} className={`${meCardClass} bg-white p-4 dark:bg-gray-900`}>
                    <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                      {Number(value || 0).toLocaleString()}
                    </p>
                    {rate !== undefined && <p className="text-xs text-gray-500">{rate}% rate</p>}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">No analytics data available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailAnalyticsPanel;
